// Привязка документа качества к конкретному экземпляру (AND-211, ТЗ-7.3).
//
// Общие для пяти форм СМК помощники: разбор привязки из ответа API, сборка полей
// для сохранения, человекочитаемая подпись и загрузка справочников с кэшем.
// Пять документов в общий каркас не сводятся (решение пользователя) — общим вынесено
// только то, что нужно эпику: сама привязка.

import axios from 'axios';

/** Производственный контур: заказ, задание, изделие, операция. */
export const SCOPE_PRODUCTION = 'production';

/** Приёмочный контур: приходная накладная и партия изготовления. */
export const SCOPE_RECEIPT = 'receipt';

/** Поля привязки, которые уходят на сохранение. Всё остальное — только для показа. */
export const BINDING_KEYS = [
  'batch_product_id',
  'production_order_id',
  'production_task_id',
  'production_task_operation_id',
  'receipt_invoice_id',
  'batch_manufacture_id',
  'nonconformity_case_id',
];

/**
 * Пустая привязка: документ можно завести вручную по одной номенклатуре,
 * все поля необязательные.
 */
export function emptyBinding() {
  return {
    batch_product_id: null,
    production_order_id: null,
    production_task_id: null,
    production_task_operation_id: null,
    receipt_invoice_id: null,
    batch_manufacture_id: null,
    nonconformity_case_id: null,
    // Только для отображения — на сохранение не уходят
    serial_number: null,
    production_order: null,
    production_task_operation: null,
    receipt_invoice: null,
    batch_manufacture: null,
  };
}

/**
 * Привязка из строки документа (или из ответа «состав расследования для коррекции»).
 */
export function bindingFromRow(row) {
  const binding = emptyBinding();
  if (!row) return binding;

  BINDING_KEYS.forEach((key) => {
    binding[key] = row[key] ?? null;
  });

  binding.serial_number = row.serial_number || row.batch_product?.serial_number || null;
  binding.production_order = row.production_order || null;
  binding.production_task_operation = row.production_task_operation || null;
  binding.receipt_invoice = row.receipt_invoice || null;
  binding.batch_manufacture = row.batch_manufacture || null;

  return binding;
}

/**
 * Поля привязки для отправки на бэкенд. Объекты и серийный номер не отправляем:
 * они приходят с сервера подгруженными и нужны только для показа.
 */
export function bindingToPayload(binding) {
  const payload = {};
  BINDING_KEYS.forEach((key) => {
    payload[key] = binding?.[key] ?? null;
  });
  return payload;
}

/** Контур привязки: производство, приёмка либо пусто. */
export function bindingScope(binding) {
  if (!binding) return null;
  if (binding.receipt_invoice_id || binding.batch_manufacture_id) return SCOPE_RECEIPT;
  if (binding.production_order_id || binding.production_task_id || binding.production_task_operation_id) {
    return SCOPE_PRODUCTION;
  }
  if (binding.batch_product_id) return SCOPE_PRODUCTION;
  return null;
}

/** Есть ли хоть одна заполненная привязка. */
export function hasBinding(binding) {
  return BINDING_KEYS.some((key) => binding?.[key]);
}

/** Дата в человеческом формате; пусто — пустая строка. */
export function formatDateRu(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/**
 * Подпись производственного заказа: номер, дата и изделие.
 *
 * Номер заказа — его собственный number, при пустом — id. Так же подписан заказ в карточке,
 * в реестре несоответствий, на столе СМК и в протоколе ПСИ. Без номера подпись схлопывалась
 * в «Производственный заказ от {дата}»: за день таких заказов бывает несколько, в селекторе
 * они неразличимы, и документ качества привязывался не к тому заказу.
 */
export function productionOrderLabel(order) {
  if (!order) return '';
  const productName = order.order_product?.product?.name || order.product?.name;
  const date = formatDateRu(order.plan_start_datetime || order.created_at);
  const number = order.number || order.id;

  const head = number ? `Заказ № ${number}` : 'Производственный заказ';

  return [date ? `${head} от ${date}` : head, productName].filter(Boolean).join(' · ');
}

/** Подпись изделия: серийный номер либо позиция изделия в заказе. */
export function batchProductLabel(serialNumber, position) {
  if (serialNumber) return `Серийный № ${serialNumber}`;
  return position ? `Изделие ${position} (серийный номер не присвоен)` : 'Изделие без серийного номера';
}

/** Подпись операции задания. */
export function taskOperationLabel(taskOperation) {
  if (!taskOperation) return '';
  return taskOperation.operation?.name || 'Операция без названия';
}

/** Подпись приходной накладной. */
export function receiptInvoiceLabel(invoice) {
  if (!invoice) return '';
  const parts = [];
  parts.push(invoice.number ? `Накладная № ${invoice.number}` : 'Накладная без номера');
  const date = formatDateRu(invoice.date);
  if (date) parts.push(`от ${date}`);
  const supplier = invoice.supplier?.name;
  return supplier ? `${parts.join(' ')} · ${supplier}` : parts.join(' ');
}

/** Подпись партии изготовления. */
export function batchManufactureLabel(batch) {
  if (!batch) return '';
  return batch.number ? `Партия ${batch.number}` : 'Партия без номера';
}

/**
 * Человекочитаемая сводка привязки — для режима просмотра и подсказки под селекторами.
 */
export function bindingSummary(binding) {
  const scope = bindingScope(binding);
  if (!scope) return 'Без привязки к изделию';

  const parts = [];
  const serial = binding.serial_number;

  if (scope === SCOPE_PRODUCTION) {
    parts.push(serial ? `Серийный № ${serial}` : 'Серийный номер не присвоен');
    const order = productionOrderLabel(binding.production_order);
    if (order) parts.push(order);
    const operation = taskOperationLabel(binding.production_task_operation);
    if (operation) parts.push(`Операция: ${operation}`);
  } else {
    const invoice = receiptInvoiceLabel(binding.receipt_invoice);
    if (invoice) parts.push(invoice);
    const batch = batchManufactureLabel(binding.batch_manufacture);
    if (batch) parts.push(batch);
    if (serial) parts.push(`Серийный № ${serial}`);
  }

  return parts.filter(Boolean).join(' · ') || 'Привязка указана';
}

// --- Справочники ---------------------------------------------------------
// Списки общие для всех строк документа, поэтому кэш живёт на уровне модуля:
// строк в таблице много, дёргать API на каждую строку нельзя.

const ordersCache = { rows: [], page: 0, lastPage: 1, promise: null };
const orderDetailCache = new Map();
const orderDetailPromises = new Map();
const invoiceDetailCache = new Map();
const invoiceDetailPromises = new Map();

/** Уже загруженные производственные заказы. */
export function loadedProductionOrders() {
  return ordersCache.rows;
}

/** Есть ли ещё непрогруженные страницы заказов. */
export function productionOrdersHasMore() {
  return ordersCache.page < ordersCache.lastPage;
}

/**
 * Догрузить очередную страницу производственных заказов.
 * Первый вызов грузит первую страницу, повторные — следующие.
 */
export async function loadMoreProductionOrders(perPage = 30) {
  if (ordersCache.promise) return ordersCache.promise;
  if (ordersCache.page >= ordersCache.lastPage && ordersCache.page > 0) return ordersCache.rows;

  const nextPage = ordersCache.page + 1;
  ordersCache.promise = axios
    .post('/api/productionOrder/getProductionOrders', { page: nextPage, per_page: perPage })
    .then(({ data }) => {
      const paginator = data?.data || {};
      const rows = paginator.data || [];
      ordersCache.rows = nextPage === 1 ? rows : [...ordersCache.rows, ...rows];
      ordersCache.page = paginator.current_page || nextPage;
      ordersCache.lastPage = paginator.last_page || ordersCache.page;
      return ordersCache.rows;
    })
    .finally(() => {
      ordersCache.promise = null;
    });

  return ordersCache.promise;
}

/**
 * Производственный заказ целиком: задания с изделиями (серийные номера) и операциями.
 * Результат кэшируется — одна и та же выборка обслуживает все строки документа.
 */
export async function loadProductionOrder(id) {
  const key = Number(id);
  if (!key) return null;
  if (orderDetailCache.has(key)) return orderDetailCache.get(key);

  if (!orderDetailPromises.has(key)) {
    const request = axios
      .post('/api/productionOrder/getProductionOrder', { id: key })
      .then(({ data }) => {
        const order = data?.data || null;
        orderDetailCache.set(key, order);
        return order;
      })
      .finally(() => orderDetailPromises.delete(key));
    orderDetailPromises.set(key, request);
  }

  return orderDetailPromises.get(key);
}

/**
 * Поиск приходных накладных (поиск по номеру и поставщику выполняет бэкенд).
 */
export async function searchReceiptInvoices(search, page = 1, perPage = 20) {
  const { data } = await axios.post('/api/receiptInvoice/getReceiptInvoices', {
    search: search || undefined,
    page,
    per_page: perPage,
  });
  const paginator = data?.data || {};
  return {
    rows: paginator.data || [],
    page: paginator.current_page || page,
    lastPage: paginator.last_page || paginator.current_page || page,
  };
}

/**
 * Приходная накладная целиком: позиции с партиями изготовления. Кэшируется по id.
 */
export async function loadReceiptInvoice(id) {
  const key = Number(id);
  if (!key) return null;
  if (invoiceDetailCache.has(key)) return invoiceDetailCache.get(key);

  if (!invoiceDetailPromises.has(key)) {
    const request = axios
      .post('/api/receiptInvoice/getReceiptInvoiceForView', { id: key })
      .then(({ data }) => {
        const invoice = data?.data || null;
        invoiceDetailCache.set(key, invoice);
        return invoice;
      })
      .finally(() => invoiceDetailPromises.delete(key));
    invoiceDetailPromises.set(key, request);
  }

  return invoiceDetailPromises.get(key);
}
